import { createAsyncThunk } from "@reduxjs/toolkit";
import { triggerOtpApi, verifyOtpApi, verifyPinApi, setPinApi, logoutApi } from "../../services/api";
import { TriggerOtpResponse } from "../../services/api/api.types";
import { MOCK_AUTH_RESPONSE } from "../../services/api/mockData";
import { API_DEFAULTS } from "../../services/api/config";

const USE_MOCKS = process.env.REACT_APP_USE_MOCKS === "true";

export const triggerOtpThunk = createAsyncThunk<
    TriggerOtpResponse,
    { mobileNumber: string; deviceId: string; consentHandle?: string },
    { rejectValue: string }
>("aa/triggerOtp", async (payload, { rejectWithValue }) => {
    if (USE_MOCKS) return MOCK_AUTH_RESPONSE as any;
    try {
        // Enforce dynamic consent handle
        if (!payload.consentHandle) {
            return rejectWithValue("Consent handle is missing. Journey cannot start.");
        }

        const res = await triggerOtpApi({
            I_MOBILENUMBER: payload.mobileNumber,
            I_BROWSER: API_DEFAULTS.BROWSER,
            I_MPIN: "", // Removed hardcoded PIN, should be handled by login flow if needed
            I_USERID: payload.mobileNumber,
            UUID: payload.deviceId,
            I_ConsentHandle: payload.consentHandle,
        });
        console.log("Trigger OTP Response:", res);
        if (res.RESULT_CODE !== "200") {
            return rejectWithValue(res.MESSAGE || "Failed to send OTP");
        }
        return { ...res, usedConsentHandle: payload.consentHandle };
    } catch (err: any) {
        return rejectWithValue(err.response?.data?.MESSAGE || "Failed to send OTP (Server Error)");
    }
});

export const verifyOtpThunk = createAsyncThunk<
    { sessionId: string; consentHandle: string; uuid?: string; existUser?: boolean },
    { mobileNumber: string; otp: string; deviceId: string; consentHandle?: string },
    { rejectValue: string }
>(
    "auth/verifyOtp",
    async (req, { rejectWithValue }) => {
        if (!req.consentHandle) {
            return rejectWithValue("Consent handle is missing. Verification halted.");
        }
        const consentHandle = req.consentHandle;

        if (USE_MOCKS) return { sessionId: MOCK_AUTH_RESPONSE.SESSION_ID, consentHandle };
        try {
            const res = await verifyOtpApi({
                I_MOBILENUMBER: req.mobileNumber,
                I_MOBOTP: req.otp,
                I_BROWSER: API_DEFAULTS.BROWSER,
                I_SESSION: null,
                I_Flag: "M",
                I_USERID: req.mobileNumber,
                UUID: req.deviceId,
                I_ConsentHandle: "",
            });

            if (res.RESULT_CODE !== "200") {
                return rejectWithValue(res.RESULT_MESSAGE || "Invalid OTP");
            }

            // In some AA flows, UUID might be returned in TriggerOTP or VerifyOTP.
            // If it's not here, we might need it from triggerOtp, let's just pass what we can or wait for user input.
            // But we will add it to the return payload so authSlice can store it if present.
            return { sessionId: res.SESSION_ID, consentHandle, uuid: (res as any).UUID, existUser: res.ExistUser };
        } catch {
            return rejectWithValue("OTP verification failed");
        }
    }
);
export const verifyPinThunk = createAsyncThunk<
    { sessionId: string; consentHandle: string; uuid?: string },
    { mobileNumber: string; pin: string; deviceId: string; consentHandle?: string },
    { rejectValue: string }
>(
    "auth/verifyPin",
    async (req, { rejectWithValue, getState }) => {
        const state = getState() as any;
        const sessionId = state.auth?.sessionId || null;

        const activeUuid = state.auth?.uuid || req.deviceId || state.auth?.deviceId;

        if (USE_MOCKS) return { sessionId: "mock-pin-session-123", consentHandle: "" };
        try {
            const res = await verifyPinApi({
                I_MOBILENUMBER: req.mobileNumber,
                I_BROWSER: API_DEFAULTS.BROWSER,
                I_MPIN: req.pin,
                I_SESSION: sessionId,
                UUID: activeUuid,
            });

            if (res.RESULT_CODE !== "200") {
                return rejectWithValue(res.RESULT_MESSAGE || res.MESSAGE || "Invalid PIN");
            }

            return { sessionId: res.SESSION_ID, consentHandle: "", uuid: (res as any).UUID };
        } catch {
            return rejectWithValue("PIN verification failed");
        }
    }
);

export const setPinThunk = createAsyncThunk<
    { sessionId: string; consentHandle: string; uuid?: string },
    { mobileNumber: string; pin: string; deviceId: string; consentHandle?: string },
    { rejectValue: string }
>(
    "auth/setPin",
    async (req, { rejectWithValue, getState }) => {
        const state = getState() as any;
        const sessionId = state.auth?.sessionId || null;
        const activeUuid = state.auth?.uuid || req.deviceId || state.auth?.deviceId;

        if (USE_MOCKS) return { sessionId: "mock-pin-session-123", consentHandle: "" };
        try {
            const res = await setPinApi({
                I_MOBILENUMBER: req.mobileNumber,
                I_BROWSER: API_DEFAULTS.BROWSER,
                I_MPIN: req.pin,
                I_SESSION: sessionId,
                UUID: activeUuid,
            });

            if (res.RESULT_CODE !== "200") {
                return rejectWithValue(res.RESULT_MESSAGE || res.MESSAGE || "Failed to set PIN");
            }

            return { sessionId: res.SESSION_ID, consentHandle: "", uuid: (res as any).UUID };
        } catch {
            return rejectWithValue("PIN setup failed");
        }
    }
);

export const logoutThunk = createAsyncThunk<
    void,
    { mobileNumber: string; deviceId: string },
    { rejectValue: string }
>(
    "auth/logout",
    async (req, { rejectWithValue, getState }) => {
        try {
            const state = getState() as any;
            const activeUuid = state.auth?.uuid || req.deviceId || state.auth?.deviceId;

            await logoutApi({
                I_MOBILENUMBER: req.mobileNumber,
                UUID: activeUuid,
            });
        } catch {
            // Silently fail logout as it's a defensive measure
            return rejectWithValue("Logout failed");
        }
    }
);
